// Helper functions for data fetching

import { hotelsAPI, roomsAPI } from '../services/api';

/**
 * Fetch hotels from API
 * @param {Function} setLoading - State setter for loading state
 * @param {Function} setHotels - State setter for hotels
 * @param {Function} setErrors - State setter for errors
 */
export const fetchHotels = async (setLoading, setHotels, setErrors) => {
  setLoading(prev => ({ ...prev, hotels: true }));
  try {
    const hotelsResponse = await hotelsAPI.getAll();
    setHotels(hotelsResponse.data.data.hotels);
    setErrors(prev => ({ ...prev, hotels: null }));
  } catch (error) {
    console.error('Error fetching hotels:', error);
    setErrors(prev => ({ ...prev, hotels: error.message || 'Failed to fetch hotels' }));
  } finally {
    setLoading(prev => ({ ...prev, hotels: false }));
  }
};

/**
 * Fetch rooms for a specific hotel
 * @param {string} hotelId - The hotel ID
 * @param {Object} loading - The loading state
 * @param {Function} setLoading - State setter for loading state
 * @param {Function} setRooms - State setter for rooms
 * @param {Function} setErrors - State setter for errors
 */
export const fetchRoomsForHotel = async (hotelId, loading, setLoading, setRooms, setErrors) => {
  // Prevent multiple simultaneous requests for the same hotel
  if (loading.rooms[hotelId]) {
    return;
  }
  
  setLoading(prev => ({
    ...prev,
    rooms: {
      ...prev.rooms,
      [hotelId]: true
    }
  }));
  
  try {
    const response = await roomsAPI.getByHotelId(hotelId);
    const hotelRooms = response.data?.data?.rooms || [];
    setRooms(prev => ({
      ...prev,
      [hotelId]: hotelRooms
    }));
    setErrors(prev => ({ ...prev, rooms: null }));
  } catch (error) {
    console.error(`Error fetching rooms for hotel ${hotelId}:`, error);
    setErrors(prev => ({ ...prev, rooms: error.message || `Failed to fetch rooms for hotel ${hotelId}` }));
  } finally {
    setLoading(prev => ({
      ...prev,
      rooms: {
        ...prev.rooms,
        [hotelId]: false
      }
    }));
  }
};

/**
 * Fetch rooms for expanded hotels
 * @param {Set} expandedHotels - The set of expanded hotels
 * @param {Array} hotels - The array of hotels
 * @param {Function} fetchRoomsForHotel - Function to fetch rooms for a hotel
 */
export const fetchRoomsForExpandedHotels = async (expandedHotels, hotels, fetchRoomsForHotel) => {
  for (const hotelId of expandedHotels) {
    await fetchRoomsForHotel(hotelId);
  }
};

export default {
  fetchHotels,
  fetchRoomsForHotel,
  fetchRoomsForExpandedHotels
};